import { Data, Player, PlayerPoints, PlayerResult, TeamGameMatch, TotalsPoints, TournamentConfig } from '../../../database.schema';
import { RYDER_CUP_TYPE, STABLEFORD_WITH_TEAM_GAMES } from '../../app/constants';
import { calculatePositions } from '../../utils/positions';
import { getTeamGameMatchResultSortedOrder } from '../../utils/team-games-score-calculator';
import { getTeamTotalsByDay } from '../../utils/totals-by-day-calculator';
import { useFetchData } from '../tournament-data';
import { TotalsPointsWithMatchResults } from './use-fetch-team-totals-by-day';

export type TourTotals = {
  tourType: string;
  totalsPointsData?: TotalsPointsData;
  individualStandings?: Array<PlayerPoints>;
  config: TournamentConfig;
};
export type TotalsPointsData = {
  team1Name: string;
  team2Name: string;
  totalsPoints: TotalsPoints;
  teamsCreated: boolean;
};

export const useFetchTotalsPoints = (selectedIndex: number) => {
  return useFetchData((data: Data): TourTotals | null => {
    if (data?.tournaments && data?.tournaments[selectedIndex]) {
      const config = { ...data?.tournaments[selectedIndex].config };
      switch (data?.tournaments[selectedIndex].type) {
        case STABLEFORD_WITH_TEAM_GAMES: {
          const tournament = data?.tournaments[selectedIndex].teamGames;
          const playerTotals: Array<PlayerPoints> = [];
          tournament?.matches
            ?.filter((match) => match.teams.every((team) => team.scoresEntered))
            .forEach((match: TeamGameMatch) => {
              const resultMatch = { ...getTeamGameMatchResultSortedOrder(match) };
              resultMatch.teams.forEach((team) =>
                team.players?.forEach((tierMatchPlayer: PlayerResult) => {
                  const playerFound = data?.players?.find((player: Player) => player.id === tierMatchPlayer.playerId);
                  if (playerFound) {
                    const totalsPlayer = playerTotals.find((playerTotal: PlayerPoints) => playerTotal.player.id === playerFound.id);
                    if (totalsPlayer) {
                      totalsPlayer.points += tierMatchPlayer.points;
                      totalsPlayer.won += team.result === 'win' ? 1 : 0;
                      totalsPlayer.halved += team.result === 'halved' ? 1 : 0;
                      totalsPlayer.lost += team.result === 'lost' ? 1 : 0;
                      totalsPlayer.roundsPlayed += 1;
                      totalsPlayer.average = Math.round((totalsPlayer.points / totalsPlayer.roundsPlayed) * 10) / 10;
                      totalsPlayer.nearestThePin += tierMatchPlayer.nearestThePin;
                      totalsPlayer.nearestThePinIn2 += tierMatchPlayer.nearestThePinIn2;
                      totalsPlayer.longestDrive += tierMatchPlayer.longestDrive;
                    } else {
                      playerTotals.push({
                        player: playerFound,
                        points: tierMatchPlayer.points,
                        won: team.result === 'win' ? 1 : 0,
                        halved: team.result === 'halved' ? 1 : 0,
                        lost: team.result === 'lost' ? 1 : 0,
                        average: tierMatchPlayer.points,
                        captainPoints: 0,
                        position: 0,
                        roundsPlayed: 1,
                        nearestThePin: tierMatchPlayer.nearestThePin,
                        nearestThePinIn2: tierMatchPlayer.nearestThePinIn2,
                        longestDrive: tierMatchPlayer.longestDrive,
                      });
                    }
                  }
                }),
              );
            });
          return {
            tourType: STABLEFORD_WITH_TEAM_GAMES,
            individualStandings: calculatePositions(playerTotals, 'points'),
            config,
          };
        }
        default: {
          const tournament = data?.tournaments[selectedIndex]?.ryderCup;
          const team1PlayerIds = tournament?.team1.playerIds;
          const team2PlayerIds = tournament?.team2.playerIds;
          const team1Name = tournament?.team1.name ?? '';
          const team2Name = tournament?.team2.name ?? '';
          const playerTotals: Array<PlayerPoints> = [];
          if (team1PlayerIds && team2PlayerIds) {
            team1PlayerIds.forEach((playerId: number) => {
              const player = data.players.find((player: Player) => player.id === playerId) ?? ({} as Player);
              playerTotals.push({
                player,
                points: 0,
                won: 0,
                lost: 0,
                halved: 0,
                average: 0,
                captainPoints: 0,
                position: 0,
                roundsPlayed: 0,
                nearestThePin: 0,
                nearestThePinIn2: 0,
                longestDrive: 0,
              });
            });
            team2PlayerIds.forEach((playerId: number) => {
              const player = data.players.find((player: Player) => player.id === playerId) ?? ({} as Player);
              playerTotals.push({
                player,
                points: 0,
                won: 0,
                lost: 0,
                halved: 0,
                average: 0,
                captainPoints: 0,
                position: 0,
                roundsPlayed: 0,
                nearestThePin: 0,
                nearestThePinIn2: 0,
                longestDrive: 0,
              });
            });
            const totalsByDay: Array<TotalsPointsWithMatchResults> = getTeamTotalsByDay(data, selectedIndex);

            if (totalsByDay.length) {
              totalsByDay.forEach((day: TotalsPointsWithMatchResults) => {
                day.totals.individual?.forEach((individualPoints: PlayerPoints) => {
                  const playerFound = playerTotals.find((playerTotal: PlayerPoints) => playerTotal.player.id === individualPoints.player.id);
                  if (playerFound) {
                    playerFound.captainPoints += individualPoints.captainPoints;
                    playerFound.won += individualPoints.won;
                    playerFound.lost += individualPoints.lost;
                    playerFound.halved += individualPoints.halved;
                    playerFound.points += individualPoints.points;
                    playerFound.roundsPlayed += 1;
                    playerFound.average = Math.round((playerFound.points / playerFound.roundsPlayed) * 10) / 10;
                    playerFound.nearestThePin += individualPoints.nearestThePin;
                    playerFound.nearestThePinIn2 += individualPoints.nearestThePinIn2;
                    playerFound.longestDrive += individualPoints.longestDrive;
                  }
                });
              });
              return {
                tourType: RYDER_CUP_TYPE,
                config,
                totalsPointsData: {
                  team1Name,
                  team2Name,
                  totalsPoints: {
                    team1Points: totalsByDay[totalsByDay.length - 1].teamRunningTotalPoints.team1Points,
                    team2Points: totalsByDay[totalsByDay.length - 1].teamRunningTotalPoints.team2Points,
                    team1Individual: playerTotals.filter((player: PlayerPoints) => team1PlayerIds.some((id: number) => id === player.player.id)),
                    team2Individual: playerTotals.filter((player: PlayerPoints) => team2PlayerIds.some((id: number) => id === player.player.id)),
                    individual: calculatePositions(playerTotals, 'average'),
                    captainsRace: calculatePositions(playerTotals, 'captainPoints'),
                  },
                  teamsCreated: true,
                },
              };
            } else {
              return {
                tourType: RYDER_CUP_TYPE,
                config,
                totalsPointsData: {
                  team1Name,
                  team2Name,
                  totalsPoints: {
                    team1Points: 0,
                    team2Points: 0,
                    team1Individual: playerTotals.filter((player: PlayerPoints) => team1PlayerIds.some((id: number) => id === player.player.id)),
                    team2Individual: playerTotals.filter((player: PlayerPoints) => team2PlayerIds.some((id: number) => id === player.player.id)),
                    individual: calculatePositions(playerTotals, 'average'),
                    captainsRace: calculatePositions(playerTotals, 'captainPoints'),
                  },
                  teamsCreated: true,
                },
              };
            }
          }
          return {
            tourType: RYDER_CUP_TYPE,
            config,
            totalsPointsData: {
              team1Name,
              team2Name,
              totalsPoints: {
                team1Points: 0,
                team2Points: 0,
                team1Individual: [],
                team2Individual: [],
                individual: [],
                captainsRace: [],
              },
              teamsCreated: false,
            },
          };
        }
      }
    }
    console.log('>>>>>>NULL');
    return null;
  });
};
